/**
 * Stock API — Stok motoru RPC çağrıları için thin wrapper.
 *
 * Postgres tarafındaki SECURITY DEFINER fonksiyonlarını (reserve_stock,
 * release_stock, commit_stock, adjust_stock) çağırır. Hata mesajlarını
 * yapılandırılmış bir StockApiError'a çevirir; UI bunları Almanca/Türkçe
 * kullanıcı mesajına dönüştürür.
 *
 * LocalStorage/Mock modunda (Supabase client yoksa) soft-fail yapar:
 *   - reserve/release/commit → sessizce "success" döner, konsola uyarı
 *   - adjust → aynı davranış
 * Bu sayede geliştirme ortamı kırılmaz; gerçek stok yalnızca Supabase
 * modunda korunur (canlı ortam zaten Supabase ile çalışıyor).
 */
import { SupabaseAdapter } from '@/api/adapters/SupabaseAdapter';

const IS_DEV = typeof import.meta !== 'undefined' && import.meta.env?.DEV;

/**
 * Stok RPC'lerinden gelen hataları yapılandırılmış forma çevirir.
 * PostgreSQL'den gelen mesaj formatı: `<code>: <detay>`.
 *
 *   insufficient_stock: product=..., available=0, requested=3
 *   product_not_found: <uuid>
 *   variant_not_found: <value> in product <uuid>
 *   invalid_quantity: -1
 *   order_not_found: <uuid>
 *   product_has_no_variants: <uuid>
 *   product_id_required
 *   order_id_required
 *   delta_zero_or_null
 *   invalid_type_for_adjust: <type>
 */
export class StockApiError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = 'StockApiError';
    this.code = code;
    this.details = details;
  }
}

function parseRpcError(error) {
  if (!error) return null;
  const raw = error.message || String(error);
  // Postgres NOTICE/ERROR önek'ini ("ERROR: " vb.) temizle.
  const clean = raw.replace(/^ERROR:\s*/i, '').replace(/^P0001:\s*/i, '').trim();
  const match = clean.match(/^([a-z_]+)(?::\s*(.+))?$/i);
  if (match) {
    return new StockApiError(match[1], clean, { raw: error });
  }
  return new StockApiError('unknown', clean, { raw: error });
}

function getClient() {
  try {
    return SupabaseAdapter.getClient();
  } catch {
    return null;
  }
}

async function callRpc(fnName, params) {
  const client = getClient();
  if (!client) {
    if (IS_DEV) {
      console.warn(`[stockApi] ${fnName} çağrıldı ama Supabase aktif değil — atlanıyor`);
    }
    return { ok: true, skipped: true, data: null };
  }

  const { data, error } = await client.rpc(fnName, params);
  if (error) throw parseRpcError(error);
  return { ok: true, skipped: false, data };
}

/**
 * Tek bir ürün kalemini rezerve eder. Yetersiz stokta hata fırlatır
 * (p_allow_negative=false default). Backorder için true geçin.
 *
 * @param {{
 *   productId: string,
 *   variantValue?: string|null,
 *   quantity: number,
 *   orderId: string,
 *   actorEmail: string,
 *   allowNegative?: boolean,
 * }} args
 * @throws {StockApiError} code='insufficient_stock' stok yetmezse
 */
export async function reserveStock({
  productId,
  variantValue = null,
  quantity,
  orderId,
  actorEmail,
  allowNegative = false,
}) {
  return callRpc('reserve_stock', {
    p_product_id: productId,
    p_variant_value: variantValue,
    p_quantity: quantity,
    p_order_id: orderId,
    p_actor_email: actorEmail,
    p_allow_negative: allowNegative,
  });
}

/**
 * Sipariş iptal edildiğinde tüm rezervleri serbest bırakır. İdempotent.
 *
 * @returns {Promise<{ ok: boolean, skipped: boolean, data: number|null }>}
 *   data = release edilen pozisyon sayısı.
 */
export async function releaseStock({ orderId, actorEmail }) {
  return callRpc('release_stock', {
    p_order_id: orderId,
    p_actor_email: actorEmail,
  });
}

/**
 * Warenausgang: siparişin pozisyonlarını gerçek stok düşüşüne çevirir.
 * reserved_stock -= qty + stock -= qty, her kalem için.
 *
 * @returns {Promise<{ ok: boolean, skipped: boolean, data: number|null }>}
 *   data = commit edilen pozisyon sayısı.
 */
export async function commitStock({ orderId, actorEmail }) {
  return callRpc('commit_stock', {
    p_order_id: orderId,
    p_actor_email: actorEmail,
  });
}

/**
 * Genel stok düzeltme: Inventur/Wareneingang/Bruch/Muster/Eigenbedarf/
 * Conversion/Adjustment.
 *
 * @param {{
 *   productId: string,
 *   variantValue?: string|null,
 *   delta: number,              // + artır, - azalt; 0 kabul edilmez
 *   type: 'inventur'|'wareneingang'|'bruch'|'muster'|'eigenbedarf'|'conversion'|'adjustment',
 *   reason?: string|null,
 *   actorEmail: string,
 *   sourceTable?: string,       // default 'manual'
 *   sourceId?: string|null,
 * }} args
 * @returns {Promise<{ ok: boolean, skipped: boolean, data: number|null }>}
 *   data = güncellenmiş stok değeri.
 */
export async function adjustStock({
  productId,
  variantValue = null,
  delta,
  type,
  reason = null,
  actorEmail,
  sourceTable = 'manual',
  sourceId = null,
}) {
  return callRpc('adjust_stock', {
    p_product_id: productId,
    p_variant_value: variantValue,
    p_delta: delta,
    p_type: type,
    p_reason: reason,
    p_actor_email: actorEmail,
    p_source_table: sourceTable,
    p_source_id: sourceId,
  });
}

/**
 * Inventur sayımını tek atomic adımda uygular. Master stock + variants
 * JSONB'yi yeni değere set eder; delta'yı stock_movements'a master-level
 * `inventur` kaydı olarak yazar. Eröffnungsinventur için `markCounted=true`
 * verildiğinde `is_counted` + metadata da doldurulur.
 *
 * Çakışma koruması: `expectedStock` (sayım başlangıcındaki stok) verilirse
 * DB tarafı "sayılan - beklenen" farkını GÜNCEL stoğa delta olarak uygular —
 * sayım sırasında işlenen satış/mal kabul hareketleri ezilmez.
 * `expectedVariants` ({value: stok}) aynı korumayı varyant bazında sağlar
 * ve DB'deki `reserved` değerlerini korur.
 *
 * @param {{
 *   productId: string,
 *   newStock: number,             // sayılan toplam
 *   newVariants?: any[]|null,     // varyantlı ürünlerde sayılan JSONB; yoksa null
 *   actorEmail: string,
 *   reason?: string,
 *   markCounted?: boolean,        // Eröffnungsinventur ise true
 *   expectedStock?: number|null,  // sayım başlangıcındaki master stok
 *   expectedVariants?: Object|null, // { [variantValue]: sayım başındaki stok }
 * }} args
 */
export async function commitInventur({
  productId,
  newStock,
  newVariants = null,
  actorEmail,
  reason = 'Inventur',
  markCounted = false,
  expectedStock = null,
  expectedVariants = null,
}) {
  return callRpc('commit_inventur', {
    p_product_id: productId,
    p_new_stock: newStock,
    p_new_variants: newVariants,
    p_actor_email: actorEmail,
    p_reason: reason,
    p_mark_counted: markCounted,
    p_expected_stock: expectedStock,
    p_expected_variants: expectedVariants,
  });
}

/**
 * Agent sipariş akışı için kullanım kolaylığı: birden çok kalemi sırayla
 * rezerve eder. Herhangi biri başarısız olursa daha önce rezerve
 * edilenleri release_stock ile otomatik geri alır ve orijinal hatayı
 * fırlatır.
 *
 * `allowNegative=true` ise insufficient_stock hatası gelmez (backorder
 * kaydedilir) ama diğer hatalar (product_not_found vb.) yine yakalanır.
 *
 * @param {{
 *   items: Array<{ productId: string, variantValue?: string|null, quantity: number }>,
 *   orderId: string,
 *   actorEmail: string,
 *   allowNegative?: boolean,
 * }} args
 */
export async function reserveOrderItems({
  items,
  orderId,
  actorEmail,
  allowNegative = false,
}) {
  const reserved = [];
  try {
    for (const it of items) {
      if (!it.productId || !it.quantity || it.quantity <= 0) continue;
      await reserveStock({
        productId: it.productId,
        variantValue: it.variantValue ?? null,
        quantity: it.quantity,
        orderId,
        actorEmail,
        allowNegative,
      });
      reserved.push(it);
    }
    return { ok: true, reserved };
  } catch (err) {
    // Rollback: bu order için zaten rezerve edilenleri tek çağrıda serbest bırak.
    try {
      await releaseStock({ orderId, actorEmail });
    } catch (releaseErr) {
      if (IS_DEV) {
        console.error('[stockApi] rollback sırasında release_stock hatası:', releaseErr);
      }
    }
    throw err;
  }
}

/**
 * Doküman numarası üretir (Postgres generate_doc_number RPC).
 * Prefix'ler: 'KM' (Kommissionierschein), 'LS' (Lieferschein), 'RE' (Rechnung).
 * Format: 'KM-2026-0001'.
 *
 * @returns {Promise<string|null>} yeni doküman numarası veya null (Supabase yoksa)
 */
export async function generateDocNumber(docType) {
  const client = getClient();
  if (!client) {
    if (IS_DEV) console.warn(`[stockApi] generate_doc_number('${docType}') atlandı — Supabase yok`);
    return null;
  }
  const { data, error } = await client.rpc('generate_doc_number', { p_doc_type: docType });
  if (error) throw parseRpcError(error);
  return data;
}

/**
 * Hata kodunu Almanca kullanıcı mesajına çevirir (UI toast'larında kullanım).
 * Almanca'yı default tuttuk çünkü mevcut UI dili Almanca.
 */
export function stockErrorToGerman(err) {
  if (!(err instanceof StockApiError)) return err?.message || 'Unbekannter Fehler';
  switch (err.code) {
    case 'insufficient_stock':
      return 'Bestand reicht nicht aus';
    case 'product_not_found':
      return 'Produkt nicht gefunden';
    case 'variant_not_found':
      return 'Variante nicht gefunden';
    case 'product_has_no_variants':
      return 'Produkt hat keine Varianten';
    case 'invalid_quantity':
      return 'Ungültige Menge';
    case 'order_not_found':
      return 'Bestellung nicht gefunden';
    case 'order_positions_invalid':
      return 'Bestellpositionen ungültig';
    case 'delta_zero_or_null':
      return 'Keine Änderung — Delta ist 0';
    case 'invalid_type_for_adjust':
      return 'Ungültiger Buchungstyp';
    default:
      return err.message;
  }
}
